import { defineStore } from 'pinia'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import type { IClosedDay } from '@team-milva/lab-models/resources'
import { ClosedDay } from '@team-milva/lab-models/resources'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'

export { ClosedDay }

export const useClosedDayStore = defineStore('closedDay', {
  state: () => ({
    allClosedDays: [] as ClosedDay[],
    unsubscribeAllClosedDays: null as (() => void) | null,
  }),

  getters: {
    allClosedDaysLoaded(): boolean {
      return !!this.unsubscribeAllClosedDays
    },

    /**
     * The lab's closed dates as plain ISO strings — what `calcDeliveryDate` and
     * the hand-over date pickers take. A getter rather than a mapped value at
     * each call site, so every consumer reads the same list.
     */
    closedDates(): string[] {
      return this.allClosedDays.map((closedDay) => closedDay.date)
    },
  },

  actions: {
    getClosedDays(): Promise<ClosedDay[]> {
      if (this.allClosedDaysLoaded) {
        return Promise.resolve(this.allClosedDays)
      }

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'ClosedDays'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allClosedDays = querySnapshot.docs
            .map((doc) => new ClosedDay(doc.data() as IClosedDay))
            // Ascending by date — the list groups by year and reads top-down
            // within a group, and `closedDates` inherits a stable order.
            .sort((a, b) => a.date.localeCompare(b.date))
          this.unsubscribeAllClosedDays = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(this.allClosedDays)
          }
        })
      })
    },

    async createClosedDay(params: { date: string; name: string }): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/closed-days',
        {
          path: { labAccountID },
          body: { date: params.date, name: params.name },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    resetStore(): void {
      this.unsubscribeAllClosedDays?.()
      this.allClosedDays = []
      this.unsubscribeAllClosedDays = null
    },

    async updateClosedDay(params: {
      closedDayID: string
      date?: string
      name?: string
    }): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/closed-days/{closedDayID}',
        {
          path: { labAccountID, closedDayID: params.closedDayID },
          body: { date: params.date, name: params.name },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async deleteClosedDay(closedDayID: string): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'delete',
        'lab-accounts/{labAccountID}/closed-days/{closedDayID}',
        {
          path: { labAccountID, closedDayID },
        }
      )
      if (!result.success) throw new Error(result.code)
    },
  },
})
